/** A photo picked on a customer's page, ready to send. Shared by the arrival
 *  page's review photos (pages/Arrived.tsx) and the photo check's added photos
 *  (preview/PhotoCheck.tsx), which shrink them the same way. */
export interface Picked {
  id: string;
  /** What will be sent: a JPEG the browser made, or the file as it was when
   *  the browser could not open it (a HEIC outside Safari), which the server
   *  converts. */
  blob: Blob;
  name: string;
  /** For the thumbnail, when the browser could draw it. */
  url: string | null;
}

/** The photo as a JPEG no longer than `longEdge`, upright. An <img> applies the
 *  phone's rotation where a bitmap read from the bytes may not, so it is drawn
 *  from one, as studio/Crop.tsx does. */
export async function shrink(file: File, longEdge: number, quality: number): Promise<Picked> {
  const id = `${file.name}-${file.size}-${file.lastModified}-${Math.random()}`;
  const source = URL.createObjectURL(file);
  try {
    const img = new Image();
    img.src = source;
    await img.decode();
    const scale = Math.min(1, longEdge / Math.max(img.naturalWidth, img.naturalHeight));
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(img.naturalWidth * scale);
    canvas.height = Math.round(img.naturalHeight * scale);
    const context = canvas.getContext("2d");
    if (!context) throw new Error("no canvas");
    context.drawImage(img, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, "image/jpeg", quality)
    );
    // A phone keeps a canvas's pixels until it is told otherwise, and five of
    // them at 2560px is most of what an older one will give a tab.
    canvas.width = 0;
    canvas.height = 0;
    if (!blob) throw new Error("no blob");
    const name = `${file.name.replace(/\.[a-z0-9]{1,5}$/i, "") || "photo"}.jpg`;
    return { id, blob, name, url: URL.createObjectURL(blob) };
  } catch {
    return { id, blob: file, name: file.name || "photo", url: null };
  } finally {
    URL.revokeObjectURL(source);
  }
}
